
import { getTeachers, saveTeacher, deleteTeacher } from '../db/teachers';
import { Teacher } from '@/types/teacher.types';
import { toast } from 'sonner';

export class TeacherBackupService {
  async exportTeachers(): Promise<Teacher[]> {
    try {
      console.log('Starting teacher data export...');
      const teachers = await getTeachers();
      console.log(`Exported ${teachers.length} teachers`);
      return Array.isArray(teachers) ? teachers : [];
    } catch (error) {
      console.error('Error exporting teachers:', error);
      throw new Error('Failed to export teacher data');
    }
  }

  async importTeachers(teachers: Teacher[], options: { overwrite?: boolean } = {}): Promise<void> {
    try {
      console.log(`Starting teacher data import for ${teachers.length} teachers...`);
      
      if (options.overwrite) {
        console.log('Clearing existing teacher data...');
        const existingTeachers = await getTeachers();
        for (const teacher of existingTeachers) {
          await deleteTeacher(teacher.id);
        }
      }

      let successCount = 0;
      let errorCount = 0;

      for (const teacher of teachers) {
        try {
          const teacherToSave: Teacher = {
            ...teacher,
            id: teacher.id || `teacher_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            createdAt: teacher.createdAt || new Date().toISOString(),
            updatedAt: new Date().toISOString()
          };
          
          await saveTeacher(teacherToSave);
          successCount++;
        } catch (error) {
          console.error(`Failed to import teacher ${teacher.firstName} ${teacher.lastName}:`, error);
          errorCount++;
        }
      }

      console.log(`Teacher import completed: ${successCount} successful, ${errorCount} failed`);
      
      if (errorCount > 0) {
        toast.warning(`Teacher import completed with ${errorCount} errors. ${successCount} teachers imported successfully.`);
      } else {
        toast.success(`Successfully imported ${successCount} teachers`);
      }
    } catch (error) {
      console.error('Error importing teachers:', error);
      throw new Error('Failed to import teacher data');
    }
  }

  async getBackupInfo(): Promise<{
    totalTeachers: number;
    totalSize: number;
    lastBackup?: string;
  }> {
    try {
      const teachers = await getTeachers();
      
      return {
        totalTeachers: teachers.length,
        totalSize: JSON.stringify(teachers).length,
        lastBackup: localStorage.getItem('last_teacher_backup') || undefined
      };
    } catch (error) {
      console.error('Error getting teacher backup info:', error);
      return {
        totalTeachers: 0,
        totalSize: 0
      };
    }
  }
}

export const teacherBackupService = new TeacherBackupService();

export const createTeacherBackup = async (): Promise<string> => {
  try {
    console.log('Starting teacher backup creation...');
    const teachers = await teacherBackupService.exportTeachers();
    
    if (!Array.isArray(teachers)) {
      throw new Error('Invalid teachers data format');
    }
    
    const backupId = `teacher_backup_${Date.now()}`;
    const backupData = {
      id: backupId,
      timestamp: new Date().toISOString(),
      teachers: teachers,
      size: JSON.stringify(teachers).length,
      description: `Teacher backup created on ${new Date().toLocaleString()}`
    };

    // Save to localStorage for local backup list
    try {
      const existingBackups = JSON.parse(localStorage.getItem('teacher_backups') || '[]');
      existingBackups.push(backupData);
      localStorage.setItem('teacher_backups', JSON.stringify(existingBackups));
      localStorage.setItem('last_teacher_backup', new Date().toISOString());
    } catch (storageError) {
      console.warn('Could not save backup to localStorage:', storageError);
    }

    // Create and download backup file
    const backupJson = JSON.stringify(backupData, null, 2);
    const blob = new Blob([backupJson], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    
    const a = document.createElement('a');
    a.href = url;
    a.download = `teachers-backup-${new Date().toISOString().split('T')[0]}.json`;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    console.log(`Teacher backup created successfully: ${teachers.length} teachers`);
    toast.success(`Teacher backup created successfully (${teachers.length} teachers)`);
    return backupId;
  } catch (error) {
    console.error('Error creating teacher backup:', error);
    toast.error('Failed to create teacher backup: ' + (error instanceof Error ? error.message : 'Unknown error'));
    throw error;
  }
};

export const restoreTeacherBackup = async (file: File): Promise<boolean> => {
  try {
    const text = await file.text();
    const backupData = JSON.parse(text);
    const teachers = backupData.teachers || backupData.data?.teachers || [];
    
    if (!Array.isArray(teachers)) {
      throw new Error('Invalid backup format: teachers data not found');
    }
    
    await teacherBackupService.importTeachers(teachers, { overwrite: true });
    return true;
  } catch (error) {
    console.error('Error restoring teacher backup:', error);
    toast.error('Failed to restore teacher backup: ' + (error instanceof Error ? error.message : 'Unknown error'));
    return false;
  }
};

export const listTeacherBackups = (): any[] => {
  try {
    return JSON.parse(localStorage.getItem('teacher_backups') || '[]');
  } catch (error) {
    console.error('Error listing teacher backups:', error);
    return [];
  }
};

export const restoreFromLocalTeacherBackup = async (backupId: string): Promise<boolean> => {
  try {
    const backups = listTeacherBackups();
    const backup = backups.find(b => b.id === backupId);
    if (!backup) {
      toast.error('Backup not found');
      return false;
    }

    await teacherBackupService.importTeachers(backup.teachers, { overwrite: true });
    return true;
  } catch (error) {
    console.error('Error restoring from local teacher backup:', error);
    toast.error('Failed to restore teacher backup');
    return false;
  }
};
